<template>
  <vx-card class="project-manager mb-base">
    <template slot="no-body">
      <div
        class="flex items-center justify-between px-6 py-4 cursor-pointer"
        @click="toggle"
      >
        <h5 class="font-semibold flex items-center">
          <feather-icon icon="SettingsIcon" svgClasses="h-5 w-5" class="mr-2" />
          Proje Yönetimi
          <span v-if="loaded" class="ml-2 text-sm font-normal text-grey">
            ({{ projects.length }} proje)
          </span>
        </h5>
        <feather-icon
          :icon="collapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'"
          svgClasses="h-5 w-5"
        />
      </div>

      <div v-show="!collapsed" class="px-6 pb-6">
        <!-- YETKILI KISILER -->
        <div class="pm-admins mb-6">
          <div class="vx-row">
            <div class="vx-col lg:w-1/2 w-full mb-4 lg:mb-0">
              <h6 class="font-semibold mb-3">
                Yetkili kişiler (superadmin) – {{ admins.length }}
              </h6>
              <div
                v-for="a in admins"
                :key="a.Id"
                class="pm-admin flex items-center mb-2"
              >
                <vs-avatar
                  :src="avatarSrc(a)"
                  :text="a.FullName"
                  size="36px"
                  class="m-0 mr-3 flex-shrink-0"
                />
                <div class="min-w-0">
                  <div class="font-semibold truncate">
                    {{ a.FullName }}
                    <vs-chip v-if="a.IsMe" color="primary" class="pm-me">Siz</vs-chip>
                    <vs-chip v-if="!a.Active" color="danger" class="pm-me">Hesap pasif</vs-chip>
                  </div>
                  <div class="text-xs text-grey truncate">
                    {{ a.Email || "—" }} · #{{ a.Id }}
                  </div>
                </div>
              </div>
              <p class="text-xs text-grey mt-2">
                Yetki <code>LoginPortal.User.Superadmin</code> rolüyle verilir;
                eklemek veya çıkarmak için RoleManagement uygulamasını kullanın.
              </p>
            </div>
            <div class="vx-col lg:w-1/2 w-full">
              <h6 class="font-semibold mb-3">Superadminler ne görür, ne yapabilir?</h6>
              <ul class="pm-rights">
                <li>
                  <b>Proje Yönetimi</b> (bu panel): öğrenciye, personele veya
                  role özel olanlar ve pasifler dahil <b>tüm projeleri</b> görür;
                  yeni proje ekler, düzenler, aktif/pasif yapar.
                </li>
                <li>
                  <b>Pasif Projeler</b> paneli: kullanıcılardan gizlenmiş kartları
                  görür ve açabilir.
                </li>
                <li><b>Proje Ekleme Rehberi</b>'ni görür.</li>
                <li>
                  <b>Gözünden Gör</b> aramasında diğer superadminler dahil herkesi
                  bulur. Diğer personel bu aramada superadminleri göremez.
                </li>
                <li>
                  Ana sayfadaki normal kartları, diğer kullanıcılar gibi kendi
                  hesap türüne ve rollerine göre görür.
                </li>
                <li>
                  Diğer kullanıcılar bu bölümlerin hiçbirini görmez; adresleri
                  doğrudan çağırsalar da sunucu reddeder.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-between mb-4">
          <vs-input
            v-model="filter"
            placeholder="Projelerde ara..."
            class="mb-2 sm:mb-0 sm:w-1/3 w-full"
          />
          <vs-button icon-pack="feather" icon="icon-plus" @click="openNew">
            Yeni Proje Ekle
          </vs-button>
        </div>

        <div class="overflow-x-auto">
          <table class="pm-table w-full">
            <thead>
              <tr>
                <th></th>
                <th>Proje</th>
                <th>Adres</th>
                <th>Kimler görür</th>
                <th>Durum</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in filteredProjects" :key="p.ProjectID">
                <td class="pm-thumb">
                  <img :src="p.ProjectImage || graphic" :alt="p.ProjectName" />
                </td>
                <td>
                  <div class="font-semibold">{{ p.ProjectName }}</div>
                  <div class="text-xs text-grey">#{{ p.ProjectID }} · {{ openTypeLabel(p) }}</div>
                </td>
                <td class="pm-url">{{ displayUrl(p) }}</td>
                <td>
                  <vs-chip v-if="p.ShowStudent" color="primary">Öğrenci</vs-chip>
                  <vs-chip v-if="p.ShowStaff" color="success">Personel</vs-chip>
                  <vs-chip v-if="p.ProjectRoleSuffix" color="warning">
                    Rol: {{ p.ProjectRoleSuffix }}
                  </vs-chip>
                  <span v-if="!p.ShowStudent && !p.ShowStaff" class="text-danger text-sm">
                    Kimse
                  </span>
                </td>
                <td>
                  <vs-button
                    size="small"
                    :color="p.IsActive ? 'success' : 'dark'"
                    :type="p.IsActive ? 'filled' : 'border'"
                    :disabled="busy"
                    :title="p.IsActive ? 'Pasife al' : 'Aktif et'"
                    @click="toggleActive(p)"
                  >
                    {{ p.IsActive ? "Aktif" : "Pasif" }}
                  </vs-button>
                </td>
                <td class="text-right">
                  <vs-button
                    size="small"
                    type="border"
                    icon-pack="feather"
                    icon="icon-edit"
                    @click="openEdit(p)"
                  >
                    Düzenle
                  </vs-button>
                </td>
              </tr>
              <tr v-if="loaded && !filteredProjects.length">
                <td colspan="6" class="text-center text-grey py-4">Proje bulunamadı.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- EKLE / DUZENLE -->
      <vs-popup
        class="pm-popup"
        :title="form.ProjectID ? 'Projeyi Düzenle' : 'Yeni Proje Ekle'"
        :active.sync="popupActive"
      >
        <div class="vx-row">
          <div class="vx-col md:w-2/3 w-full">
            <vs-input
              v-model="form.ProjectName"
              label="Proje adı *"
              class="w-full mb-4"
              maxlength="130"
            />

            <label class="vs-input--label">Açılış şekli</label>
            <select v-model="form.openType" class="pm-select w-full mb-4">
              <option value="link">Bağlantı – yeni sekmede açılır</option>
              <option value="sso">Otomatik giriş (SSO) – yazılım ekibiyle kurulur</option>
              <option value="popup">Açılır pencere – HTML/iframe içeriği</option>
              <option value="fullurl">Tam adres – ProjectHtml alanındaki adres</option>
            </select>

            <template v-if="form.openType === 'link' || form.openType === 'sso'">
              <vs-input
                v-model="form.ProjectBaseUrl"
                :label="form.openType === 'sso' ? 'Site alan adı *' : 'Site adresi *'"
                :placeholder="form.openType === 'sso' ? 'ornek.fsm.edu.tr' : 'https://lms.fsm.edu.tr/lisanstalep'"
                class="w-full mb-1"
              />
              <p class="text-xs text-grey mb-4">
                <template v-if="form.openType === 'sso'">
                  Dönüş adresinin alan adıyla aynı olmalı (https:// olmadan).
                </template>
                <template v-else>
                  Tam adres yazabilirsiniz; "https://" otomatik eklenir.
                </template>
              </p>
            </template>

            <template v-if="form.openType === 'sso'">
              <vs-input
                v-model="form.ProjectReturnUrl"
                label="Dönüş adresi (ReturnUrl) *"
                placeholder="https://ornek.fsm.edu.tr/giris"
                class="w-full mb-4"
              />
              <label class="vs-input--label">SSO türü</label>
              <select v-model.number="form.ModuleType" class="pm-select w-full mb-4">
                <option :value="0">Gateway çerezi (aynı ana alan adı)</option>
                <option :value="1">EBYS</option>
                <option :value="4">Farklı alan adı (gateway token)</option>
              </select>
              <label class="vs-input--label">Kullanıcı hangi bilgiyle tanıtılsın?</label>
              <select v-model.number="form.PropGroup" class="pm-select w-full mb-4">
                <option :value="0">Kullanıcı adı</option>
                <option :value="1">Portal ID</option>
                <option :value="2">External ID</option>
                <option :value="3">LDAP ID</option>
                <option :value="4">E-posta</option>
                <option :value="5">TC kimlik no</option>
                <option :value="6">Öğrenci no</option>
              </select>
            </template>

            <template v-if="form.openType === 'popup'">
              <vs-input
                v-model="form.ProjectDesc"
                label="Pencere başlığı"
                class="w-full mb-4"
              />
              <vs-textarea
                v-model="form.ProjectHtml"
                label="HTML içeriği *"
                height="140px"
                class="w-full mb-4"
              />
            </template>

            <template v-if="form.openType === 'fullurl'">
              <vs-input
                v-model="form.ProjectHtml"
                label="Açılacak tam adres *"
                placeholder="https://..."
                class="w-full mb-4"
              />
            </template>

            <label class="vs-input--label">Kimler görsün?</label>
            <div class="flex flex-wrap mb-4 mt-1">
              <vs-checkbox v-model="form.ShowStudent" class="mr-6">
                Öğrenciler (TÖMER dahil)
              </vs-checkbox>
              <vs-checkbox v-model="form.ShowStaff">
                Akademisyen ve personel
              </vs-checkbox>
            </div>

            <div class="flex items-center mb-4">
              <vs-switch v-model="form.IsActive" class="mr-3" />
              <span>{{ form.IsActive ? "Aktif – kullanıcılara görünür" : "Pasif – sadece superadmin görür" }}</span>
            </div>

            <div
              class="text-primary cursor-pointer text-sm mb-3 select-none"
              @click="showAdvanced = !showAdvanced"
            >
              {{ showAdvanced ? "▾" : "▸" }} Gelişmiş ayarlar
            </div>
            <div v-show="showAdvanced">
              <vs-input
                v-model="form.ProjectRoleSuffix"
                label="Rol kısıtı (uygulama adı)"
                placeholder="Örn. CarParking – boşsa herkes"
                class="w-full mb-1"
              />
              <p class="text-xs text-grey mb-4">
                Doluysa kart yalnızca bu uygulamada rolü olanlara görünür.
              </p>
              <vs-input
                v-model="form.SeoUrl"
                label="Kısa ad (SeoUrl)"
                placeholder="/login/<kısa-ad> ile yönlendirme için"
                class="w-full mb-4"
              />
            </div>
          </div>

          <div class="vx-col md:w-1/3 w-full">
            <label class="vs-input--label">Görsel</label>
            <div class="pm-preview mb-3">
              <img :src="form.ProjectImage || graphic" alt="önizleme" />
            </div>
            <input
              ref="file"
              type="file"
              accept="image/*"
              class="hidden"
              @change="onFile"
            />
            <vs-button
              size="small"
              type="border"
              icon-pack="feather"
              icon="icon-upload"
              class="w-full mb-2"
              @click="$refs.file.click()"
            >
              Görsel yükle
            </vs-button>
            <vs-button
              v-if="form.ProjectImage"
              size="small"
              type="flat"
              color="danger"
              class="w-full mb-2"
              @click="form.ProjectImage = null"
            >
              Görseli kaldır
            </vs-button>
            <p class="text-xs text-grey">
              Görsel doğrudan kaydedilir; sunucuya dosya yüklemek gerekmez.
              En fazla 1 MB. Şeffaf arka planlı SVG/PNG önerilir.
            </p>
            <vs-input
              :value="isDataUri ? '' : form.ProjectImage"
              @input="form.ProjectImage = $event"
              label="veya görsel adresi"
              placeholder="https://... veya /logos/x.svg"
              class="w-full mt-3"
            />
          </div>
        </div>

        <div class="flex justify-end mt-4">
          <vs-button type="flat" color="dark" class="mr-3" @click="popupActive = false">
            Vazgeç
          </vs-button>
          <vs-button :disabled="busy" icon-pack="feather" icon="icon-save" @click="save">
            Kaydet
          </vs-button>
        </div>
      </vs-popup>
    </template>
  </vx-card>
</template>

<script>
import DashApi from "@/api/dash";

const MAX_IMAGE_BYTES = 1024 * 1024;

function emptyForm() {
  return {
    ProjectID: 0,
    ProjectName: "",
    ProjectDesc: "",
    ProjectHtml: "",
    ProjectBaseUrl: "",
    ProjectReturnUrl: "",
    ProjectImage: null,
    ShowStudent: true,
    ShowStaff: true,
    ModuleType: 0,
    PropGroup: 0,
    ProjectRoleSuffix: "",
    SeoUrl: "",
    IsActive: true,
    openType: "link",
  };
}

function openTypeOf(p) {
  if (p.ProjectReturnUrl) return "sso";
  if (p.ModuleType === 2) return "popup";
  if (p.ModuleType === 3) return "fullurl";
  return "link";
}

export default {
  name: "ProjectManager",
  data() {
    return {
      collapsed: true,
      loaded: false,
      busy: false,
      projects: [],
      admins: [],
      filter: "",
      popupActive: false,
      showAdvanced: false,
      form: emptyForm(),
      graphic: "/blank_logo.png",
    };
  },
  computed: {
    filteredProjects() {
      const q = this.filter.trim().toLocaleLowerCase("tr");
      if (!q) return this.projects;
      return this.projects.filter((p) =>
        [p.ProjectName, p.ProjectBaseUrl, p.ProjectReturnUrl]
          .filter(Boolean)
          .some((v) => v.toLocaleLowerCase("tr").includes(q))
      );
    },
    isDataUri() {
      return (this.form.ProjectImage || "").startsWith("data:");
    },
  },
  methods: {
    toggle() {
      this.collapsed = !this.collapsed;
      if (!this.collapsed && !this.loaded) {
        this.load();
        this.loadAdmins();
      }
    },
    // Fotografi olmayanlarda adres ".../resim/CV/" gibi bos klasorle biter.
    avatarSrc(a) {
      const pic = a.ProfilePicture;
      return pic && !pic.endsWith("/") ? pic : null;
    },
    async loadAdmins() {
      const response = await DashApi.getSuperAdmins(this);
      if (response && Array.isArray(response.data)) this.admins = response.data;
    },
    async load() {
      const response = await DashApi.getAllProjects(this);
      if (response && Array.isArray(response.data)) {
        this.projects = response.data;
        this.loaded = true;
      } else this.notifyError(response && response.data && response.data.error);
    },
    openTypeLabel(p) {
      return {
        link: "Bağlantı",
        sso: "SSO",
        popup: "Açılır pencere",
        fullurl: "Tam adres",
      }[openTypeOf(p)];
    },
    displayUrl(p) {
      const t = openTypeOf(p);
      if (t === "fullurl") return p.ProjectHtml;
      if (t === "popup") return "—";
      if (t === "sso") return p.ProjectReturnUrl;
      return p.ProjectBaseUrl ? "https://" + p.ProjectBaseUrl : "";
    },
    openNew() {
      this.form = emptyForm();
      this.showAdvanced = false;
      this.popupActive = true;
    },
    openEdit(p) {
      this.form = Object.assign(emptyForm(), p, {
        ProjectDesc: p.ProjectDesc || "",
        ProjectHtml: p.ProjectHtml || "",
        ProjectBaseUrl: p.ProjectBaseUrl || "",
        ProjectReturnUrl: p.ProjectReturnUrl || "",
        ProjectRoleSuffix: p.ProjectRoleSuffix || "",
        SeoUrl: p.SeoUrl || "",
        openType: openTypeOf(p),
      });
      this.showAdvanced = !!(p.ProjectRoleSuffix || p.SeoUrl);
      this.popupActive = true;
    },
    onFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      if (file.size > MAX_IMAGE_BYTES) {
        this.notifyError("Görsel 1 MB'dan büyük olamaz.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => (this.form.ProjectImage = reader.result);
      reader.readAsDataURL(file);
    },
    buildPayload(f) {
      const payload = {
        ProjectID: f.ProjectID || 0,
        ProjectName: f.ProjectName,
        ProjectDesc: f.ProjectDesc,
        ProjectHtml: f.ProjectHtml,
        ProjectBaseUrl: f.ProjectBaseUrl,
        ProjectReturnUrl: null,
        ProjectImage: f.ProjectImage,
        ShowStudent: !!f.ShowStudent,
        ShowStaff: !!f.ShowStaff,
        ModuleType: f.ModuleType,
        PropGroup: f.PropGroup,
        ProjectRoleSuffix: f.ProjectRoleSuffix,
        SeoUrl: f.SeoUrl,
        IsActive: !!f.IsActive,
      };
      if (f.openType === "link") payload.ModuleType = 0;
      else if (f.openType === "popup") payload.ModuleType = 2;
      else if (f.openType === "fullurl") payload.ModuleType = 3;
      else if (f.openType === "sso") payload.ProjectReturnUrl = f.ProjectReturnUrl;
      return payload;
    },
    async submit(payload) {
      this.busy = true;
      try {
        const response = await DashApi.saveProject(payload, this);
        if (response && response.data && response.data.success) {
          await this.load();
          this.$emit("changed");
          return true;
        }
        this.notifyError(response && response.data && response.data.error);
        return false;
      } finally {
        this.busy = false;
      }
    },
    async save() {
      if (!this.form.ShowStudent && !this.form.ShowStaff && this.form.IsActive) {
        const ok = window.confirm(
          "Kimler görsün seçilmedi; aktif olsa bile kart kimseye görünmeyecek. Yine de kaydedilsin mi?"
        );
        if (!ok) return;
      }
      if (await this.submit(this.buildPayload(this.form))) {
        this.popupActive = false;
        this.$vs.notify({
          time: 2000,
          title: "Kaydedildi",
          text: this.form.ProjectName,
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
      }
    },
    async toggleActive(p) {
      if (this.busy) return;
      const payload = this.buildPayload(
        Object.assign(emptyForm(), p, { openType: openTypeOf(p) })
      );
      payload.IsActive = !p.IsActive;
      await this.submit(payload);
    },
    notifyError(text) {
      this.$vs.notify({
        time: 3000,
        title: "Hata",
        text: text || "İşlem tamamlanamadı.",
        color: "danger",
        iconPack: "feather",
        icon: "icon-alert-circle",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.project-manager {
  border-left: 4px solid #28c76f;
}

.pm-table {
  border-collapse: collapse;

  th,
  td {
    padding: 0.6rem 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    text-align: left;
    vertical-align: middle;
  }

  th {
    font-weight: 600;
    font-size: 0.85rem;
    color: #626262;
  }

  .pm-thumb img {
    width: 56px;
    height: 36px;
    object-fit: contain;
  }

  .pm-url {
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.85rem;
  }
}

.pm-admins {
  background: rgba(0, 0, 0, 0.025);
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;

  .pm-me {
    transform: scale(0.8);
    margin: 0;
    vertical-align: middle;
  }

  .pm-rights {
    list-style: disc;
    padding-left: 1.25rem;
    font-size: 0.9rem;
    line-height: 1.55;

    li {
      margin-bottom: 0.35rem;
    }
  }

  code {
    background: rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    padding: 0 4px;
  }
}

.pm-select {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  padding: 0.55rem 0.6rem;
  background: transparent;
  color: inherit;
}

.pm-preview {
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 1rem;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;

  img {
    max-width: 90%;
    max-height: 90%;
    object-fit: contain;
  }
}
</style>

<style lang="scss">
.pm-popup .vs-popup {
  width: 760px !important;
  max-width: 95vw;
}
</style>
